import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CustomModal, MaxTagComponent } from '../../PricingSimulator/Modals/CreateEditProjectContainer';
import { colorConstants } from 'config/colorConstants';
import { Input, Spin, Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { antIcon } from '../../Filter/FilterApplyButton';
import { CustomSelect } from '../../PricingSimulator/DimSection';
import { TPODataContext, initialPlanEditMetaData } from 'contexts/TPOContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingSimulatorUrls, tradePromoOptimizationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { compareUsers, generateErrorMsg, getConfig, sendNotification } from 'utils/helperMethods';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';

import dayjs, { Dayjs } from 'dayjs';

//updating antd config from moment to dayjs
//antd uses moment js by default for reading value
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { queryClient } from 'services/reactQueryService';
import { UserContext } from 'contexts/ConfigContext';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import _ from 'lodash';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import { CreatePlanModalType } from 'components/organism/TradePromoOptimization/Plans';
import { TPO_UI_LIMITS } from 'constants/tpo';
//updated component that uses dayjs instead of moment
const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

export const constantWidth = 300;
export const characterLimit = 7;

type Props = {
    open: boolean;
    isEdit: boolean;
    data: CreatePlanModalType['data'];
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish: Function;
};

const CreateEditPlanContainer: FC<Props> = ({ open, onCancel, onSubmitFinish, data, isEdit }) => {
    const { dimensionConfigData, setPlanEditMetaData, planEditMetaData } = useContext(TPODataContext);
    const dim1Attribute = dimensionConfigData?.dim1Attribute;
    const dim2Attribute = dimensionConfigData?.dim2Attribute;

    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const enableEditMode = tpoConfig?.enableEditMode;
    const limit = tpoConfig?.attributesListLimit;
    const planVersionDropdownOptions = tpoConfig?.planVersionDropdownOptions;
    const [planName, setPlanName] = useState('');
    const [yearOfPlan, setYearOfPlan] = useState<number>(new Date().getFullYear());
    const [dim1Attributes, setDim1Attributes] = useState<any[]>([]);
    const [dim2Attributes, setDim2Attributes] = useState<any[]>([]);
    const [dim2AttributeVariable, setDim2AttributesVariable] = useState<any>({});
    const [dim1AttributeVariable, setDim1AttributesVariable] = useState<any>({});
    const [dim1SearchValue, setDim1SearchValue] = useState<string>('');
    const [dim2SearchValue, setDim2SearchValue] = useState<string>('');
    const [planVersion, setPlanVersion] = useState<any>();
    const [baselineVolumeVersion, setBaselineVolumeVersion] = useState<any>();
    const [isBaselineVersionEditable, setIsBaselineVersionEditable] = useState<boolean>(true);
    const [hasEditingRights, setHasEditingRights] = useState<any>(false);
    const modalTitle = isEdit ? 'Edit Plan' : 'Create Plan';
    const modalButtonTitle = isEdit ? 'Save Plan' : 'Create Plan';

    function validateFields() {
        if (!planName?.trim()) return true;
        if (!yearOfPlan) return true;
        if (dim1Attribute && dim1Attributes?.length === 0) return true;
        if (dim2Attribute && dim2Attributes?.length === 0) return true;
        if (!planVersion) return true;
        if (!baselineVolumeVersion) return true;
        return false;
    }

    function resetAllFields() {
        setPlanName('');
        setYearOfPlan(new Date().getFullYear());
        setDim1Attributes([]);
        setDim2Attributes([]);
        setDim1SearchValue('');
        setDim2SearchValue('');
        setPlanVersion(undefined);
        setBaselineVolumeVersion(undefined);
        setIsBaselineVersionEditable(true);
        setHasEditingRights(false);
    }
    //reset all fields to default when popup closes
    useEffect(() => {
        if (open && isEdit) {
            if (enableEditMode) editPlanOnClickHandler();
            else setHasEditingRights(true);
            setPlanName(data?.plan_name);
            setPlanVersion(planVersionDropdownOptions?.find((item: any) => item?.value === data?.draft_plan_version));
            setDim1Attributes(data?.dim1_attribute_value);
            setDim2Attributes([data?.dim2_attribute_value]);
            setYearOfPlan(data?.plan_year);
        }
        if (!open) {
            resetAllFields();

            //clear cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['tpo-get-attribute-types'],
            });
            queryClient.removeQueries({
                exact: false,
                queryKey: ['tpo-get-baseline-volume-versions'],
            });
        }
    }, [open]);

    const { mutate: editPlanAcquireFn, loading: editPlanAcquireLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${data?.plan_id}/edit/acquire`,
        method: 'post',
    });

    function editPlanOnClickHandler() {
        if (editPlanAcquireLoading) return;
        const payload = {
            user_id: localStorage.getItem('email'),
        };
        editPlanAcquireFn(payload, {
            onSuccess: (data) => {
                //if user is same, then it means that edit rights are acquired
                if (compareUsers(data?.data?.edit_lock?.editing_user)) {
                    setHasEditingRights(true);
                    sendNotification('success', tpoNotificationMessages.PLAN_EDIT_ACQUIRE_SUCCESS);
                } else {
                    sendNotification(
                        'error',
                        `User ${data?.data?.edit_lock?.editing_user} is currently editing this plan`,
                    );
                }
                setPlanEditMetaData({
                    editingUser: data?.data?.edit_lock?.editing_user,
                    sessionExpiresInSecs: data?.data?.edit_lock?.session_expires_in_sec,
                    startedAt: data?.data?.edit_lock?.started_at,
                    planId: data?.data?.plan_id,
                });
            },
            onError: (error: any) => {
                const errorMsg = generateErrorMsg(
                    String(error?.response?.data?.detail || ''),
                    ErrorMessages.GENERALIZED_ERROR_MSG,
                );
                //show error message
                sendNotification('error', errorMsg);
            },
        });
    }

    //dim1 dropdown variable
    useEffect(() => {
        setDim1AttributesVariable({
            attribute_type: dim1Attribute?.key,
            limit: limit,
            offset: 0,
            filters: null,
            dimension: dim1Attribute?.dimension,
            search_string: dim1SearchValue,
        });
    }, [dim1Attribute?.key]);

    //get attribute types - dim 1  - category
    const {
        data: dim1List,
        loading: dim1ListLoading,
        error: dim1ListError,
        count: dim1ListCount,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && Boolean(dim1Attribute) },
        queryKey: ['tpo-get-attribute-types', 'tpo-get-attribute-types-dim1', dim1AttributeVariable],
        url: `${tradePromoOptimizationUrls.getPlanFilters}`,
        variables: { ...dim1AttributeVariable },
        parserFunction: (res: any) => {
            if (res?.data?.length === 0) {
                throw 'No Data';
            }

            const tickedAttributes: any[] = [...(dim1Attributes ?? [])];
            const result = res?.data?.map((item: any) => {
                if (item?.ticked) {
                    tickedAttributes.push(item?.label);
                }
                return {
                    label: item?.label,
                    value: item?.label,
                };
            });
            setDim1Attributes(tickedAttributes);
            //inf scroller
            if (dim1AttributeVariable?.offset > 0 && dim1List?.length > 0) return [...dim1List, ...result];
            return result;
        },
    });
    const filterObj = useMemo(() => {
        let tempObj: any = {
            // inclusion: [],
            // exclusion: [],
        };
        if (dim1Attribute?.dimension) {
            tempObj[dim1Attribute?.dimension] = { inclusion: [], exclusion: [] };
        }
        if (dim2Attribute?.dimension) {
            tempObj[dim2Attribute?.dimension] = { inclusion: [], exclusion: [] };
        }

        if (!dim1Attributes || !dim1Attribute?.key) return (tempObj = null);

        //multi select
        if (Array.isArray(dim1Attributes) && dim1Attributes.length > 0) {
            tempObj[dim1Attribute?.dimension].inclusion = dim1Attributes?.map((attr: any) => {
                return {
                    attributeType: dim1Attribute?.key,
                    attributeDesc: attr,
                    attributeExId: attr,
                    dimension: dim1Attribute?.dimension,
                };
            });
        }
        //single select
        if (typeof dim1Attributes === 'string') {
            tempObj[dim1Attribute?.dimension].inclusion = [
                {
                    attributeType: dim1Attribute?.key,
                    attributeDesc: dim1Attributes,
                    attributeExId: dim1Attributes,
                    dimension: dim1Attribute?.dimension,
                },
            ];
        }
        return tempObj;
    }, [dim1Attributes]);
    //dim2 dropdown variable

    useEffect(() => {
        setDim2AttributesVariable({
            attribute_type: dim2Attribute?.key,
            limit: limit,
            offset: 0,
            dimension: dim2Attribute?.dimension,
            filters: filterObj,
            search_string: dim2SearchValue,
        });
    }, [dim2Attribute?.key, filterObj]);

    //get attribute types - dim 2  - BU
    const {
        data: dim2List,
        loading: dim2ListLoading,
        error: dim2ListError,
        count: dim2ListCount,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && Boolean(dim2Attribute) },
        queryKey: ['tpo-get-attribute-types', 'tpo-get-attribute-types-dim2', dim2AttributeVariable],
        url: `${tradePromoOptimizationUrls.getPlanFilters}`,
        variables: { ...dim2AttributeVariable },
        parserFunction: (res: any) => {
            if (res?.data?.length === 0) {
                throw 'No Data';
            }
            const tickedAttributes: any[] = [...(dim2Attributes ?? [])];
            const result = res?.data?.map((item: any) => {
                if (item?.ticked) {
                    tickedAttributes.push(item?.label);
                }
                return {
                    label: item?.label,
                    value: item?.label,
                };
            });
            setDim2Attributes(tickedAttributes);
            //inf scroller
            if (dim2AttributeVariable?.offset > 0 && dim2List?.length > 0) return [...dim2List, ...result];
            return result;
        },
    });

    // Check if we should fetch baseline volume versions
    const shouldFetchBaselineVersions = open && dim1Attributes?.length > 0 && dim2Attributes?.length > 0 && yearOfPlan;

    // Build query params for baseline volume versions API
    const baselineVersionQueryParams = useMemo(() => {
        let queryString = `dim1_attribute_val=${dim1Attributes[0]}&dim2_attribute_val=${dim2Attributes[0]}&plan_year=${yearOfPlan}`;
        if (isEdit && data?.plan_id) {
            queryString += `&plan_id=${data.plan_id}`;
        }
        return queryString;
    }, [dim1Attributes, dim2Attributes, yearOfPlan, isEdit, data?.plan_id]);

    // Get baseline volume versions from API
    const { data: baselineVolumeVersionOptions, loading: baselineVolumeVersionLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(shouldFetchBaselineVersions) },
        queryKey: [
            'tpo-get-baseline-volume-versions',
            dim1Attributes,
            dim2Attributes,
            yearOfPlan,
            isEdit,
            data?.plan_id,
        ],
        url: `${tradePromoOptimizationUrls.getBaselineVolumeVersionsUrl}?${baselineVersionQueryParams}`,
        parserFunction: (res: any) => {
            const versions = res?.versions || [];
            const isEditable = res?.is_baseline_version_editable ?? true;
            setIsBaselineVersionEditable(isEditable);

            if (versions.length === 0) {
                sendNotification(
                    'error',
                    'No baseline volume versions available for the selected criteria. Cannot create plan.',
                );
                return [];
            }

            const options = versions.map((item: any) => ({
                label: item?.value,
                value: item?.key,
            }));

            return options;
        },
    });

    // In edit mode, auto-select baseline version when options are loaded
    useEffect(() => {
        if (isEdit && data?.baseline_volume_version && baselineVolumeVersionOptions?.length > 0) {
            const selectedOption = baselineVolumeVersionOptions.find(
                (opt: any) => opt.value === data.baseline_volume_version,
            );
            if (selectedOption) {
                setBaselineVolumeVersion(selectedOption);
            }
        }
    }, [isEdit, data?.baseline_volume_version, baselineVolumeVersionOptions]);

    // Reset baseline volume version when dim1, dim2, or year changes (only for create mode)
    useEffect(() => {
        if (!isEdit) {
            setBaselineVolumeVersion(undefined);
        }
    }, [dim1Attributes, dim2Attributes, yearOfPlan, isEdit]);

    //create plan api
    const { mutate: createPlanFn, loading: createPlanLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.createPlanUrl,
        method: 'post',
    });

    //update plan api
    const { mutate: updatePlanFn, loading: updatePlanLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.updatePlanUrl}/${data?.plan_id}`,
        method: 'post',
    });

    const createEditFunction = isEdit ? updatePlanFn : createPlanFn;
    const createEditLoading = isEdit ? updatePlanLoading : createPlanLoading;
    //called when clicked on create plan button
    const onSubmit = () => {
        if (createEditLoading || disableAllFields) return;

        if (planName?.length > TPO_UI_LIMITS.PLAN_NAME_MAX_LENGTH) {
            sendNotification('error', `Plan name cannot exceed ${TPO_UI_LIMITS.PLAN_NAME_MAX_LENGTH} characters`);
            return;
        }

        let dim1Variable = {};
        if (dim1Attribute) {
            dim1Variable = {
                dim1: {
                    [dim1Attribute?.key]: dim1Attributes,
                },
            };
        }
        let dim2Variable = {};
        if (dim2Attribute) {
            dim2Variable = {
                dim2: {
                    [dim2Attribute?.key]: dim2Attributes,
                },
            };
        }
        let payload = {};
        if (isEdit) {
            payload = {
                plan_name: planName?.trim(),
                plan_version: planVersion?.value,
                baseline_volume_version: baselineVolumeVersion?.value,
            };
        } else {
            payload = {
                plan_name: planName?.trim(),
                created_by: localStorage.getItem('email'),
                dim1_attribute_value: dim1Attributes, //accepts array always
                dim2_attribute_val: tpoConfig?.enableMultiSelectDim2 ? dim2Attributes : dim2Attributes?.[0],
                plan_year: yearOfPlan,
                plan_type: null,
                plan_version: planVersion?.value,
                baseline_volume_version: baselineVolumeVersion?.value,
            };
        }

        createEditFunction(
            { ...payload },
            {
                onSuccess: (data) => {
                    onSubmitFinish(data?.data?.plan_id || null);
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail),
                        ErrorMessages.GENERALIZED_ERROR_MSG,
                    );
                    //show error message
                    sendNotification('error', errorMsg);
                },
            },
        );
    };

    //list inf scroller function for dim1 nested - material group/product
    const dim2OnInfiniteScrollOnNextMemoised = useCallback(() => {
        if (dim2ListLoading) return;
        const newOffset = dim2AttributeVariable?.offset + dim2AttributeVariable?.limit;
        setDim2AttributesVariable({ ...dim2AttributeVariable, offset: newOffset });
    }, [dim2AttributeVariable, dim2ListLoading]);

    //list inf scroller function for dim1 nested - material group/product
    const dim1OnInfiniteScrollOnNextMemoised = useCallback(() => {
        if (dim1ListLoading) return;
        const newOffset = dim1AttributeVariable?.offset + dim1AttributeVariable?.limit;
        setDim1AttributesVariable({ ...dim1AttributeVariable, offset: newOffset });
    }, [dim1AttributeVariable, dim1ListLoading]);

    function handleSearch(val: string, from: 'dim1' | 'dim2') {
        if (from === 'dim1') {
            setDim1AttributesVariable({ ...dim1AttributeVariable, offset: 0, search_string: val });
        } else {
            setDim2AttributesVariable({ ...dim2AttributeVariable, offset: 0, search_string: val });
        }
    }

    //debounced search handler
    const debouncedHandleSearch = useCallback(_.debounce(handleSearch, 400), [
        dim1AttributeVariable,
        dim2AttributeVariable,
    ]);

    //generate plan name from year, category and customer
    useEffect(() => {
        if (yearOfPlan && dim1Attributes?.length && dim2Attributes?.length && !isEdit) {
            const planName = `${yearOfPlan}_${dim1Attributes?.join('_')}_${dim2Attributes?.join('_')}`;
            setPlanName(planName);
        }
    }, [dim1Attributes, dim2Attributes, yearOfPlan]);

    //disable if the user has no access to edit it
    const disableAllFields = isEdit && !hasEditingRights;
    const isSubmitButtonDisabled = validateFields() || disableAllFields;

    return (
        <div>
            <CustomModal
                bodyStyle={{ background: colorConstants.greyAlt9 }}
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{ style: { borderRadius: 6 }, disabled: isSubmitButtonDisabled }}
                okText={createEditLoading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : modalButtonTitle}
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                width={650}
                onCancel={(e) => {
                    if (createEditLoading) return;
                    onCancel(e);
                }}
                title={modalTitle}
            >
                <div className="flex-col flex gap-6">
                    {editPlanAcquireLoading ? (
                        <div className="h-[254px]">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center">
                                <p className="text font-xs mr-5 w-[180px] text-right text-blackAlt">
                                    <span className="text-redAlt">* </span>Plan Name :
                                </p>
                                <Input
                                    value={planName}
                                    type="text"
                                    disabled={
                                        !yearOfPlan ||
                                        !dim1Attributes?.length ||
                                        !dim2Attributes?.length ||
                                        disableAllFields
                                    }
                                    onChange={(e) => setPlanName(e.target.value)}
                                    style={{ fontSize: 13, width: constantWidth, borderRadius: 6 }}
                                    placeholder="Plan Name"
                                />
                            </div>
                            {dim1Attribute && (
                                <div className="flex items-center">
                                    <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                        <span className="text-redAlt">* </span>
                                        {dim1Attribute?.label}
                                        {tpoConfig?.enableMultiSelectDim1 ? '(s)' : ''}{' '}
                                        {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                                    </p>
                                    <SelectWithInfiniteScroller
                                        hasMore={dim1List?.length < dim1ListCount}
                                        onInfiniteScrollOnNextMemoised={dim1OnInfiniteScrollOnNextMemoised}
                                        loading={dim1ListLoading}
                                        disabled={isEdit || disableAllFields}
                                        style={{ width: constantWidth }}
                                        mode={tpoConfig?.enableMultiSelectDim1 ? 'multiple' : undefined}
                                        placeholder={`Select ${dim1Attribute?.label}`}
                                        maxTagCount={2}
                                        maxTagPlaceholder={(val) => {
                                            const remainingCount = val.length || 0;
                                            return <MaxTagComponent remainingCount={remainingCount} />;
                                        }}
                                        filterOption={false}
                                        showArrow
                                        autoClearSearchValue={false}
                                        searchValue={dim1SearchValue}
                                        showSearch
                                        onSearch={(val) => {
                                            setDim1SearchValue(val);
                                            debouncedHandleSearch(val, 'dim1');
                                        }}
                                        notFoundContent={
                                            dim1ListLoading ? (
                                                <div className="p-3">
                                                    <Loader />{' '}
                                                </div>
                                            ) : (
                                                <ErrorBox msg="No Data" />
                                            )
                                        } // Show loader
                                        maxTagTextLength={characterLimit}
                                        value={dim1Attributes}
                                        onChange={(val: any, item) => {
                                            const value = tpoConfig?.enableMultiSelectDim1 ? val : [val];

                                            setDim1Attributes(value);
                                        }}
                                        options={dim1List}
                                    />
                                </div>
                            )}

                            {dim2Attribute && (
                                <div className="flex items-center">
                                    <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                        <span className="text-redAlt">* </span>
                                        {dim2Attribute?.label}
                                        {tpoConfig?.enableMultiSelectDim2 ? '(s)' : ''}{' '}
                                        {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                                    </p>
                                    <SelectWithInfiniteScroller
                                        hasMore={dim2List?.length < dim2ListCount}
                                        onInfiniteScrollOnNextMemoised={dim2OnInfiniteScrollOnNextMemoised}
                                        style={{ width: constantWidth }}
                                        mode={tpoConfig?.enableMultiSelectDim2 ? 'multiple' : undefined}
                                        placeholder={`Select ${dim2Attribute?.label}`}
                                        maxTagCount={2}
                                        maxTagPlaceholder={(val) => {
                                            const remainingCount = val.length || 0;
                                            return <MaxTagComponent remainingCount={remainingCount} />;
                                        }}
                                        filterOption={false}
                                        showArrow
                                        showSearch
                                        searchValue={dim2SearchValue}
                                        notFoundContent={
                                            dim2ListLoading ? (
                                                <div className="p-3">
                                                    <Loader />{' '}
                                                </div>
                                            ) : (
                                                <ErrorBox msg="No Data" />
                                            )
                                        } // Show loader
                                        onSearch={(val) => {
                                            setDim2SearchValue(val);
                                            debouncedHandleSearch(val, 'dim2');
                                        }}
                                        maxTagTextLength={characterLimit}
                                        value={dim2Attributes}
                                        onChange={(val, item) => {
                                            const value = tpoConfig?.enableMultiSelectDim2 ? val : [val];

                                            setDim2Attributes(value);
                                        }}
                                        options={dim2List}
                                        loading={dim2ListLoading}
                                        disabled={isEdit || disableAllFields}
                                    />
                                </div>
                            )}
                            <div className="flex items-center">
                                <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                    <span className="text-redAlt">* </span>
                                    Year of Planning :
                                </p>
                                <DatePicker
                                    disabled={isEdit || disableAllFields}
                                    allowClear={false}
                                    value={dayjs().year(yearOfPlan ?? new Date().getFullYear())}
                                    style={{ width: constantWidth, borderRadius: 6 }}
                                    onChange={(val, date) => {
                                        setYearOfPlan(Number(date));
                                    }}
                                    disabledDate={(date) => {
                                        //enable current year and year + 1
                                        return !(date.year() === dayjs().year() || date.year() === dayjs().year() + 1);
                                    }}
                                    picker="year"
                                />
                            </div>
                            <div className="flex items-center">
                                <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                    <span className="text-redAlt">* </span> Draft Plan Version :
                                </p>
                                <CustomSelect
                                    disabled={disableAllFields}
                                    style={{ width: constantWidth }}
                                    placeholder="Multiple Select"
                                    showArrow
                                    showSearch={false}
                                    value={planVersion?.value}
                                    onChange={(val: any, item) => {
                                        setPlanVersion(item);
                                    }}
                                    options={planVersionDropdownOptions}
                                />
                            </div>
                            <div className="flex items-center">
                                <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                    <span className="text-redAlt">* </span> Baseline Vol Version :
                                </p>
                                <CustomSelect
                                    disabled={
                                        disableAllFields ||
                                        !dim1Attributes?.length ||
                                        !dim2Attributes?.length ||
                                        !yearOfPlan ||
                                        !isBaselineVersionEditable
                                    }
                                    loading={baselineVolumeVersionLoading}
                                    style={{ width: constantWidth }}
                                    placeholder="Select Baseline Vol Version"
                                    showArrow
                                    showSearch={false}
                                    value={baselineVolumeVersion?.value}
                                    onChange={(val: any, item) => {
                                        setBaselineVolumeVersion(item);
                                    }}
                                    options={baselineVolumeVersionOptions || []}
                                />
                                {isEdit && !isBaselineVersionEditable && (
                                    <Tooltip
                                        title="To modify the Baseline volume version, delete all existing promotions first."
                                        placement="top"
                                        color="white"
                                        overlayStyle={{ borderRadius: 4 }}
                                        overlayInnerStyle={{ color: colorConstants.darkGrey }}
                                    >
                                        <ExclamationCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: colorConstants.purpleFont,
                                                fontSize: 16,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </CustomModal>
        </div>
    );
};

export default CreateEditPlanContainer;
